import React from "react";
import { Helmet } from "react-helmet-async";
import { BRAND } from "../../config/locations";

// ---------------------------------------------------------------------------
// Shared per-location SEO + JSON-LD helpers.
// Used by both the rich Sidcup page and the generic LocationPage so schema and
// meta tags are generated from the same config in one place.
// ---------------------------------------------------------------------------

/** Build the absolute canonical URL for a location. */
export const locationUrl = (location) => `${BRAND.url}/${location.slug}`;

/**
 * Build a schema.org SportsActivityLocation object from a location config.
 * Only includes fields that have data (address pieces, geo, phone, hours).
 */
export const buildLocationJsonLd = (location) => {
	const addr = location.address || {};
	const hasAddress =
		addr.streetAddress && !String(addr.streetAddress).startsWith("TODO");

	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "SportsActivityLocation",
		"@id": `${locationUrl(location)}#location`,
		name: location.name,
		url: locationUrl(location),
		email: location.contactEmail || BRAND.email,
		telephone: location.phone || BRAND.phone,
	};

	if (location.seo?.description) {
		jsonLd.description = location.seo.description;
	}

	if (hasAddress) {
		jsonLd.address = {
			"@type": "PostalAddress",
			streetAddress: addr.streetAddress,
			addressLocality: addr.addressLocality,
			addressRegion: addr.addressRegion,
			postalCode:
				addr.postalCode && !String(addr.postalCode).startsWith("TODO")
					? addr.postalCode
					: undefined,
			addressCountry: addr.addressCountry,
		};
	}

	if (location.geo && location.geo.latitude != null) {
		jsonLd.geo = {
			"@type": "GeoCoordinates",
			latitude: location.geo.latitude,
			longitude: location.geo.longitude,
		};
	}

	if (Array.isArray(location.openingHours) && location.openingHours.length) {
		jsonLd.openingHoursSpecification = location.openingHours.map((oh) => ({
			"@type": "OpeningHoursSpecification",
			dayOfWeek: oh.days,
			opens: oh.opens,
			closes: oh.closes,
		}));
	}

	if (location.pricing?.currency) {
		jsonLd.currenciesAccepted = location.pricing.currency;
	}

	if (location.priceRange) {
		jsonLd.priceRange = location.priceRange;
	}

	if (location.aggregateRating?.ratingValue) {
		jsonLd.aggregateRating = {
			"@type": "AggregateRating",
			ratingValue: location.aggregateRating.ratingValue,
			reviewCount: location.aggregateRating.reviewCount,
		};
	}

	return jsonLd;
};

/** Build a schema.org FAQPage object from a location's faq list (or null). */
export const buildFaqJsonLd = (location) => {
	if (!Array.isArray(location.faq) || location.faq.length === 0) return null;
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: location.faq.map((item) => ({
			"@type": "Question",
			name: item.q,
			acceptedAnswer: { "@type": "Answer", text: item.a },
		})),
	};
};

/**
 * <Helmet> block for a location: title, description, keywords, canonical,
 * optional geo meta, and the SportsActivityLocation JSON-LD.
 */
export const LocationSeo = ({ location }) => {
	const seo = location.seo || {};
	const url = locationUrl(location);
	const jsonLd = buildLocationJsonLd(location);
	const faqJsonLd = buildFaqJsonLd(location);
	const hasGeo = location.geo && location.geo.latitude != null;

	return (
		<Helmet>
			{seo.title && <title>{seo.title}</title>}
			{seo.title && <meta name="title" content={seo.title} />}
			{seo.description && <meta name="description" content={seo.description} />}
			{seo.keywords && <meta name="keywords" content={seo.keywords} />}
			<link rel="canonical" href={url} />

			{/* Open Graph */}
			<meta property="og:type" content="website" />
			<meta property="og:url" content={url} />
			{seo.title && <meta property="og:title" content={seo.title} />}
			{seo.description && (
				<meta property="og:description" content={seo.description} />
			)}

			{/* Twitter */}
			<meta property="twitter:card" content="summary_large_image" />
			<meta property="twitter:url" content={url} />
			{seo.title && <meta property="twitter:title" content={seo.title} />}
			{seo.description && (
				<meta property="twitter:description" content={seo.description} />
			)}

			{/* Geo meta (only when coordinates are known) */}
			{hasGeo && (
				<meta
					name="geo.placename"
					content={location.address?.addressLocality || ""}
				/>
			)}
			{hasGeo && (
				<meta
					name="geo.position"
					content={`${location.geo.latitude};${location.geo.longitude}`}
				/>
			)}
			{hasGeo && (
				<meta
					name="ICBM"
					content={`${location.geo.latitude}, ${location.geo.longitude}`}
				/>
			)}

			<script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
			{faqJsonLd && (
				<script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
			)}
		</Helmet>
	);
};

export default LocationSeo;
